import ScreenNameType from "@navigation/nav-screens";
import { useReduxDevToolsExtension } from "@react-navigation/devtools";
import { DefaultTheme, LinkingOptions, NavigationContainer } from "@react-navigation/native";
import { NavigationState } from "@react-navigation/routers";
import { createStackNavigator } from "@react-navigation/stack";
import { fetchListAccountLoginRequest, setHasContactId } from "app/modules/account-management/reducer/actions";
import { getStatusListAccountLoginSelector, getUserProfileSelector } from "app/modules/account-management/reducer/selectors";
import { createSessionId } from "app/modules/login/login.utils";
import { fetchLoginSuccess } from "app/modules/login/reducer/actions";
import { hideModal, showModal } from "app/modules/modal-management/reducer/action";
import CustomSplashScreen from "app/modules/splash/splash-screen";
import { ErrorBoundary } from "app/shared/components/error-boundary/error-boundary";
import { SideMenuProvider } from "app/shared/components/side-menu/SideMenuProvider";
import useLogin from "app/shared/hook/useLogin";
import { setSessionID } from "app/shared/store/config-store/actions";
import { Event, recordEvent } from "app/shared/util/analytic";
import { pushAppFlyer, pushCleverTapEvent, pushCustomerAppFlyer, pushCustomerCleverTap } from "app/shared/util/app-flyer";
import { navigationRef } from "app/shared/util/root-navigator";
import React, { Suspense } from "react";
import { useDispatch, useSelector } from "react-redux";
import useLogout from "../../shared/hook/useLogout/useLogout";
import { AuthStack, CommonStack } from "../root-stack";
import { UserStack } from "../user-stack";
import { fetchSystemIDRequest } from "app/shared/store/devices/actions";
import { generatePasswordSha1 } from "app/shared/util/pcrypto";
import { covertPhoneNumVietNam } from "app/shared/util/validate";

export const Stack = createStackNavigator();
export const getAuthScreens = () => {
  return AuthStack.map((screen, index) => (
    <Stack.Screen name={screen.name} component={screen.component} options={screen.options} key={index} />
  ));
};

const navTheme = {
  ...DefaultTheme,
  colors: {
    ...DefaultTheme.colors,
    background: "transparent",
  },
};

const getCommonScreens = () => {
  return CommonStack.map((screen, index) => (
    <Stack.Screen name={screen.name} component={screen.component} options={screen.options} key={index} />
  ));
};

const linking: LinkingOptions = {
  prefixes: ["pvcombankapp://"],
  config: {
    initialRouteName: ScreenNameType.HOME,
    screens: {
      [ScreenNameType.HOME]: "/",
    },
  },
};

function NavContainer() {
  const isAuth = useLogin();
  const dispatch = useDispatch();
  const [redirectToHome, setRedirectToHome] = React.useState(false);
  const statusListAccountLogin = useSelector(getStatusListAccountLoginSelector);
  const userProfileStore = useSelector(getUserProfileSelector);

  const handlerParamsAppFlyer = () => {
    console.log("handlerParamsAppFlyer");
    const param = {
      success: true,
      failure: false,
    };
    return {
      eventName: "af_login",
      params: {
        af_channel: "ib_rb",
        af_login_status: JSON.stringify(param),
      },
    };
  };

  const handlerParamsCleverTap = (contact: string) => {
    console.log("handlerParamsCleverTap");
    // Đăng nhập | Ghi nhớ | Đăng ký
    return {
      eventNameCleverTap: "ct_login",
      paramsCleverTap: {
        ct_cta: "Đăng nhập",
        ct_channel: "ib_rb",
        ct_login_status: "success",
        ct_cccd_ID: contact,
      },
    };
  };

  React.useEffect(() => {
    if (isAuth) {
      const sessionID = createSessionId();
      dispatch(setSessionID(sessionID));
      dispatch(fetchLoginSuccess());
      dispatch(fetchListAccountLoginRequest());
      dispatch(fetchSystemIDRequest());
    }
  }, [dispatch, isAuth]);

  React.useEffect(() => {
    if (isAuth && userProfileStore?.IDNUMBER?.length) {
      generatePasswordSha1(userProfileStore?.IDNUMBER).then((value) => {
        console.log("generatePasswordSha1: ", value);
        dispatch(setHasContactId(value));
        const { eventName, params } = handlerParamsAppFlyer();
        const { eventNameCleverTap, paramsCleverTap } = handlerParamsCleverTap(value);
        const cellPhone = covertPhoneNumVietNam(userProfileStore?.CELLPHONE);
        const userProfile = {
          Name: userProfileStore.FULLNAME || "", // String
          Identity: value, // String or number
          Email: userProfileStore?.EMAIL || "",
          Phone: "+" + cellPhone || "", // +84 ***
          Gender: userProfileStore?.GENDER === "MALE" ? "M" : "F",
        };
        pushAppFlyer(eventName, params);
        pushCustomerAppFlyer(value || "");
        pushCustomerCleverTap(userProfile);
        pushCleverTapEvent(eventNameCleverTap, paramsCleverTap);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth, userProfileStore]);

  React.useEffect(() => {
    if (statusListAccountLogin === "ok") {
      setRedirectToHome(true);
    } else {
      setRedirectToHome(false);
    }
  }, [statusListAccountLogin]);

  if (!redirectToHome) return <CustomSplashScreen />;
  return <AppWebRouter />;
}

function AppWebRouter() {
  useLogout();
  const routeNameRef = React.useRef<string>();
  useReduxDevToolsExtension(navigationRef);

  const onStateChanged = (_state: NavigationState | undefined) => {
    try {
      const previousRouteName = String(routeNameRef.current);
      const currentRouteName = String(navigationRef.current?.getCurrentRoute()?.name);
      console.table([{ previousRouteName, currentRouteName }]);
      if (previousRouteName !== currentRouteName) {
        recordEvent({ eventName: Event.NAVIGATE, attributes: { currentRouteName } });
      }
      // Save the current route name for later comparison
      routeNameRef.current = currentRouteName;
    } catch (error) {
      console.log("Analytics Error", error);
    }
  };

  const getUserDrawerScreens = () => {
    return UserStack.map((screen, index) => (
      <Stack.Screen options={screen.options} name={screen.name} component={screen.component} key={index} />
    ));
  };

  return (
    <ErrorBoundary>
      <SideMenuProvider>
        <NavigationContainer
          documentTitle={{
            formatter: (_options, _route) => "PVcomBank",
          }}
          linking={linking}
          ref={navigationRef}
          theme={navTheme}
          independent={true}
          onStateChange={onStateChanged}
          onReady={() => {
            routeNameRef.current = navigationRef.current?.getCurrentRoute()?.name;
          }}>
          <Suspense fallback={<Loading />}>
            <Stack.Navigator headerMode="none" initialRouteName={ScreenNameType.HOME}>
              {getUserDrawerScreens()}
              {getCommonScreens()}
            </Stack.Navigator>
          </Suspense>
        </NavigationContainer>
      </SideMenuProvider>
    </ErrorBoundary>
  );
}

const Loading = () => {
  const dispatchStore = useDispatch();
  React.useEffect(() => {
    dispatchStore(showModal({ modalType: "loading", modalProps: { open: true } }));
    return () => {
      dispatchStore(hideModal());
    };
  }, [dispatchStore]);
  return <></>;
};

export default NavContainer;
